/**
 * Uncaught errors in the SPA, reported to the API (#88).
 *
 * Until this, an exception a bartender hit went to the browser console and
 * nowhere else. Now an uncaught error or an unhandled promise rejection is
 * posted to `POST /v1/client-errors`, which logs it in the same structured
 * shape as the API's own errors. The organisation and the person come from
 * the token and the `x-baros-org` header the request already carries, so
 * the report itself adds only what the server cannot know: the screen, the
 * venue being acted at, and the error.
 *
 * What it sends is a whitelist — kind, name, message, stack, screen, venue —
 * never the state of a screen, so a spec on screen cannot ride along.
 *
 * It is careful not to become the problem. A render loop that throws on every
 * frame would otherwise post hundreds of identical reports a second, so the
 * same error is sent once per window, the rate is capped, and a page has a
 * lifetime budget. A report that cannot be sent is dropped: there is nowhere
 * better to put it, and retrying would only add load to whatever is failing.
 */

import { authHeaders } from "./acting.js";

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export type ClientErrorKind = "error" | "unhandledrejection";

/** Exactly the body the route accepts. */
export interface ClientErrorReport {
  readonly kind: ClientErrorKind;
  readonly name: string;
  readonly message: string;
  readonly stack?: string;
  readonly screen: string;
  readonly venueId?: string;
}

/** The server clips at these too; clipping here keeps a report under the body cap. */
const MAX_MESSAGE = 1000;
const MAX_STACK = 4000;

export interface ReporterLimits {
  /** How long the same error is suppressed after it is sent once. */
  readonly dedupeMs: number;
  /** At most this many reports in any `windowMs`. */
  readonly maxPerWindow: number;
  readonly windowMs: number;
  /** And at most this many for the life of the page. */
  readonly maxPerPage: number;
}

export const DEFAULT_LIMITS: ReporterLimits = {
  dedupeMs: 5 * 60_000,
  maxPerWindow: 5,
  windowMs: 60_000,
  maxPerPage: 30,
};

/**
 * Same kind, name, message and first stack frame: the same error, for the
 * purpose of not reporting it twice. The screen is deliberately left out, so
 * a fault in shared code reports once rather than once per screen.
 */
export function fingerprint(report: ClientErrorReport): string {
  const frame = report.stack?.split("\n").find((line) => /^\s*at\s|@/.test(line))?.trim() ?? "";
  return [report.kind, report.name, report.message, frame].join("|");
}

/**
 * The limiter, apart from the browser so it can be tested with a fake
 * clock. Returns whether the report was handed to `send`.
 */
export function createReporter(
  send: (report: ClientErrorReport) => void,
  now: () => number = Date.now,
  limits: ReporterLimits = DEFAULT_LIMITS,
): (report: ClientErrorReport) => boolean {
  const lastSent = new Map<string, number>();
  let recent: number[] = [];
  let total = 0;

  return (report) => {
    const at = now();
    if (total >= limits.maxPerPage) return false;

    const key = fingerprint(report);
    const previous = lastSent.get(key);
    if (previous !== undefined && at - previous < limits.dedupeMs) return false;

    recent = recent.filter((t) => at - t < limits.windowMs);
    if (recent.length >= limits.maxPerWindow) return false;

    recent.push(at);
    lastSent.set(key, at);
    total += 1;
    try {
      send(report);
    } catch {
      // Sending is best effort; a reporter that throws would report itself.
    }
    return true;
  };
}

const clip = (v: string, max: number) => (v.length > max ? v.slice(0, max) : v);

/**
 * A report from whatever surfaced, or null when there is nothing to report.
 *
 * An `Error` gives its name, message and stack. A rejection with a string
 * reason gives the string. Anything else — a rejected plain object, say —
 * is described by its type and not serialised, because its contents are
 * exactly what the whitelist keeps out.
 *
 * "Script error." with no error object is a cross-origin script failing:
 * the browser withholds everything about it, so a report would say nothing
 * and, from browser extensions, would say it often.
 */
export function reportFrom(
  kind: ClientErrorKind,
  value: unknown,
  fallbackMessage: string,
  screen: string,
  venueId: string | null,
): ClientErrorReport | null {
  let name = "Error";
  let message: string;
  let stack: string | undefined;

  if (value instanceof Error) {
    name = value.name || "Error";
    message = value.message || fallbackMessage;
    stack = value.stack;
  } else if (typeof value === "string" && value.trim() !== "") {
    message = value;
  } else if (value === undefined || value === null) {
    message = fallbackMessage;
  } else {
    name = "NonError";
    const type = typeof value;
    const article = /^[aeiou]/.test(type) ? "An" : "A";
    message = `${article} ${type} was ${kind === "unhandledrejection" ? "rejected" : "thrown"}`;
  }

  if (!message || (kind === "error" && message === "Script error." && !(value instanceof Error))) return null;
  return {
    kind,
    name: clip(name, 100),
    message: clip(message, MAX_MESSAGE),
    ...(stack ? { stack: clip(stack, MAX_STACK) } : {}),
    screen: /^[\w-]{1,64}$/.test(screen) ? screen : "unknown",
    ...(venueId ? { venueId } : {}),
  };
}

/** Where the app is, read at the moment of the error rather than at install. */
export interface ReporterContext {
  readonly token: string;
  readonly screen: string;
  readonly venueId: string | null;
}

/**
 * Listen for uncaught errors and unhandled rejections for the life of the
 * page. Returns the function that stops listening.
 *
 * Nothing is sent without a token: the route refuses an unauthenticated
 * report, and before sign-in there is no one to attach it to.
 */
export function installErrorReporter(context: () => ReporterContext): () => void {
  const report = createReporter((body) => {
    const { token } = context();
    void fetch(`${BASE}/v1/client-errors`, {
      method: "POST",
      headers: { ...authHeaders(token), "content-type": "application/json" },
      body: JSON.stringify(body),
      // So a report from a page being closed or reloaded still goes out.
      keepalive: true,
    }).catch(() => undefined);
  });

  const handle = (kind: ClientErrorKind, value: unknown, fallback: string) => {
    const { token, screen, venueId } = context();
    if (!token) return;
    const body = reportFrom(kind, value, fallback, screen, venueId);
    if (body) report(body);
  };

  const onError = (event: ErrorEvent) => handle("error", event.error, event.message);
  const onRejection = (event: PromiseRejectionEvent) =>
    handle("unhandledrejection", event.reason, "Unhandled promise rejection");

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
